import { ANALYTICS_EVENTS } from "@/features/analytics"
import { PalmCaptureStep } from "../components/steps/palm-capture-step"
import { ageFromDob } from "./event-params"
import type { QuizConfig } from "../types"

/**
 * "Quiz Simplified" (Figma "Dark Mode" board 3635:46742). The palmistry flow
 * with the left/right palm photo-capture steps included. Counted progress is
 * 8 questions (name → email); the analysis screen is not counted.
 *
 * Named GA4 events (Trello QZm294ZE): `event`/`eventOnShow` fire from the
 * engine; the per-source photo events + the checkout events are pointwise (see
 * `palm-capture-step.tsx` / `checkout-default.tsx`).
 */
export const simplifiedQuiz: QuizConfig = {
  id: "simplified",
  name: "Quiz Simplified",
  steps: [
    {
      id: "name",
      eventOnShow: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_LOADED,
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_NAME,
      type: "input",
      title: "What's your name?",
      subtitle: "We'll use this to personalize your experience",
      placeholder: "Enter your name",
      required: true,
      validation: {
        pattern: "^(?=.*\\p{L})[\\p{L} '’-]{3,}$",
        message: "Please enter a valid name (3+ letters, no numbers or symbols)",
      },
    },
    {
      id: "gender",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_GENDER,
      eventParams: (a) => ({ gender: a.gender }),
      type: "single-choice",
      layout: "icons",
      title: "What's your gender?",
      subtitle: "This helps us shape your readings more accurately",
      required: true,
      options: [
        { label: "Male", value: "male", icon: "mars" },
        { label: "Female", value: "female", icon: "venus" },
        { label: "Other", value: "other", icon: "ellipsis" },
      ],
    },
    {
      id: "dob",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a) }),
      type: "date",
      dateMode: "dob",
      title: "What's your date of birth?",
      subtitle: "We'll use this to calculate your zodiac and birth chart",
      required: true,
    },
    {
      id: "tob",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_BIRTHTIME,
      eventParams: (a) => ({ select: a.tob === "unknown" ? "skipped" : "entered" }),
      type: "time",
      title: "Do you know your birth time?",
      subtitle: "This helps reveal a more accurate birth chart and personal reading",
      required: true,
      secondaryAction: { label: "I don't remember", value: "unknown" },
    },
    {
      id: "marital",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_STATUS,
      type: "single-choice",
      layout: "list",
      title: "What's your marital status?",
      subtitle: "We'll use this to tailor your relationship reading",
      required: true,
      options: [
        { label: "Free", value: "free" },
        { label: "Soulmate", value: "soulmate" },
        { label: "Married", value: "married" },
        { label: "Difficult", value: "difficult" },
      ],
    },
    {
      id: "left-palm",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PHOTO_LEFT_PROCESSED,
      type: "photo",
      title: "Take a photo of your left palm",
      component: PalmCaptureStep,
      fullBleed: true,
    },
    {
      id: "right-palm",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PHOTO_RIGHT_PROCESSED,
      type: "photo",
      title: "Take a photo of your right palm",
      component: PalmCaptureStep,
      fullBleed: true,
    },
    {
      id: "analysis",
      eventOnShow: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PHOTO_PROCESSING,
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PRESUB,
      type: "analysis",
      title: "Analyzing the unique shape of your palm",
      countsTowardProgress: false,
      checklist: [
        "Scanning your palm structure",
        "Detecting key lines and markers",
        "Interpreting your natural energy patterns",
      ],
    },
    {
      id: "email",
      event: ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_EMAIL,
      type: "email",
      title: "Where should we send your personal report?",
      placeholder: "Enter your email",
      required: true,
      legalNote: true,
    },
  ],
}
